import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { TouchableInputProps } from '..';
import { BASE_URL } from '../../../../config';
import { analyticsTrackEvent } from '../../../../core/analytics';
import { ACTION_VACCINE_SEARCH, HOMEPAGE } from '../../../../core/analytics/events';
import { isClientSide } from '../../../../core/util/system';
import { useRequestBrowserPosition } from '../../../../hooks/geo/useBrowserPosition';
import { SolvReduxState } from '../../../../reducers';
import LocationInput from '../LocationInput';
import SearchButton from '../SearchButton';
import VaccineInput from '../VaccineInput';

/**
 * The search form for a covid test.
 */
const VaccineForm = ({ touched }: TouchableInputProps) => {
  const { searchLocation, position } = useSelector((state: SolvReduxState) => ({
    searchLocation: state.searchPreferences.location,
    position: state.position,
  }));

  const { requestPosition } = useRequestBrowserPosition();

  const [vaccine, setVaccine] = useState('any');
  const [awaitingPosition, setAwaitingPosition] = useState(false);

  const coords = useMemo(() => {
    const latitude = searchLocation?.latitude ?? position?.result?.latitude;
    const longitude = searchLocation?.longitude ?? position?.result?.longitude;

    if (!latitude || !longitude) return null;
    return { latitude, longitude };
  }, [
    position?.result?.latitude,
    position?.result?.longitude,
    searchLocation?.latitude,
    searchLocation?.longitude,
  ]);

  const directoryUrl = useMemo(() => {
    const res: any = { ...coords };

    if (vaccine !== 'any') {
      res.vaccine_type = vaccine;
    }

    const params = new URLSearchParams(res);
    return `${BASE_URL}/dir/covid-vaccine/latlng?${params.toString()}`;
  }, [coords, vaccine]);

  const search = useCallback(() => {
    if (!coords && !awaitingPosition) {
      setAwaitingPosition(true);
      requestPosition();
      return;
    }

    if (isClientSide() && coords) {
      analyticsTrackEvent(HOMEPAGE, { action: ACTION_VACCINE_SEARCH, vaccine });
      window.location.href = directoryUrl;
    }
  }, [awaitingPosition, coords, directoryUrl, requestPosition, vaccine]);

  useEffect(() => {
    if (awaitingPosition && coords) {
      setAwaitingPosition(false);
      search();
    }
  }, [awaitingPosition, coords, search]);

  return (
    <>
      <LocationInput hideDivider touched={touched} />
      <VaccineInput collapseOnMobile onChange={setVaccine} />
      <SearchButton disabled={awaitingPosition && !coords} onSearch={search} />
    </>
  );
};
export default VaccineForm;
